import { UserRole, UserIndustry } from '../contexts/UserPreferencesContext';

interface ScenarioForScoring {
  id: string;
  title: string;
  difficulty?: string;
  categories?: string[];
  tags?: string[];
  isAvailable: boolean;
}

export interface ScoredScenario {
  scenario: ScenarioForScoring;
  score: number;
  matchReasons: string[];
}

/**
 * ScenarioRecommendationService
 *
 * Scores and ranks scenarios based on user preferences (role & industry).
 * Scoring breakdown:
 * - Role match (difficulty field): 50 points
 * - Industry match (tags/categories): 40 points
 * - Availability bonus: 10 points
 */
export class ScenarioRecommendationService {
  private static instance: ScenarioRecommendationService;

  private constructor() {}

  static getInstance(): ScenarioRecommendationService {
    if (!ScenarioRecommendationService.instance) {
      ScenarioRecommendationService.instance = new ScenarioRecommendationService();
    }
    return ScenarioRecommendationService.instance;
  }

  /**
   * Get industry from scenario tags/categories
   */
  private getScenarioIndustry(scenario: ScenarioForScoring): UserIndustry {
    const allTags = [...(scenario.categories || []), ...(scenario.tags || [])].map(t => t.toLowerCase());

    if (allTags.includes('healthcare') || allTags.includes('hipaa')) return 'healthcare';
    if (allTags.includes('fintech') || allTags.includes('financial') || allTags.includes('payment')) return 'fintech';
    if (allTags.includes('technology') || allTags.includes('saas') || allTags.includes('software')) return 'technology';

    // Fallback to title matching
    const title = scenario.title.toLowerCase();
    if (title.includes('health')) return 'healthcare';
    if (title.includes('financial') || title.includes('fintech')) return 'fintech';
    if (title.includes('tech')) return 'technology';

    return 'other';
  }

  /**
   * Get role from scenario difficulty field
   */
  private getScenarioRole(scenario: ScenarioForScoring): UserRole {
    const difficulty = (scenario.difficulty || '').toLowerCase();
    if (difficulty === 'tech') return 'tech';
    if (difficulty === 'exec') return 'exec';
    return null;
  }

  /**
   * Score a single scenario based on user preferences
   */
  scoreScenario(
    scenario: ScenarioForScoring,
    userRole: UserRole,
    userIndustry: UserIndustry
  ): ScoredScenario {
    let score = 0;
    const matchReasons: string[] = [];

    // Role match: 50 points
    const scenarioRole = this.getScenarioRole(scenario);
    if (userRole && scenarioRole === userRole) {
      score += 50;
      matchReasons.push(`Designed for ${userRole === 'tech' ? 'Technical Leaders' : 'Executives'}`);
    }

    // Industry match: 40 points
    const scenarioIndustry = this.getScenarioIndustry(scenario);
    if (userIndustry && userIndustry !== 'other' && scenarioIndustry === userIndustry) {
      score += 40;
      const industryLabel = {
        healthcare: 'Healthcare',
        fintech: 'FinTech',
        technology: 'Technology',
      }[userIndustry];
      matchReasons.push(`${industryLabel} industry focus`);
    }

    // Availability bonus: 10 points
    if (scenario.isAvailable) {
      score += 10;
    }

    return {
      scenario,
      score,
      matchReasons,
    };
  }

  /**
   * Score and rank all scenarios, returning top recommendations
   */
  getRecommendations(
    scenarios: ScenarioForScoring[],
    userRole: UserRole,
    userIndustry: UserIndustry,
    maxRecommendations: number = 2
  ): ScoredScenario[] {
    // Score all scenarios
    const scoredScenarios = scenarios.map(scenario =>
      this.scoreScenario(scenario, userRole, userIndustry)
    );

    // Sort by score (descending), then by availability
    scoredScenarios.sort((a, b) => {
      // Primary: score
      if (b.score !== a.score) return b.score - a.score;
      // Secondary: availability
      if (a.scenario.isAvailable !== b.scenario.isAvailable) {
        return a.scenario.isAvailable ? -1 : 1;
      }
      return 0;
    });

    // Return top recommendations that have at least some match
    return scoredScenarios
      .filter(s => s.score > 10) // Must have more than just availability bonus
      .slice(0, maxRecommendations);
  }

  /**
   * Check if a scenario matches user preferences
   */
  isRecommended(
    scenario: ScenarioForScoring,
    userRole: UserRole,
    userIndustry: UserIndustry
  ): boolean {
    const scored = this.scoreScenario(scenario, userRole, userIndustry);
    return scored.score > 10; // More than just availability bonus
  }
}

// Export singleton instance
export const scenarioRecommendationService = ScenarioRecommendationService.getInstance();
